import type { GameState, LogEntry, PlayerStats, EquipmentInstance } from '../types/game';
import { validateGameState, migrateV1ToV2, migrateV2ToV3, migrateV3ToV4, migrateV4ToV5, migrateV5ToV6, migrateV6ToV7 } from './save-schema';
import { addXpToPlayer } from '../game/skills';
import { getVesselDefById, getVesselTripProfit } from '../game/vessels';
import { fmtCoins } from '../ui/format';

const SAVE_KEY = 'fishing-game-save-v1';
const SAVE_DEBOUNCE_MS = 500;

function createDefaultPlayerStats(): PlayerStats {
  return {
    level: 1, xp: 0, xpToNext: 100, unallocatedPoints: 0,
    skills: {
      fishing: { level: 1 },
      bartering: { level: 1 },
      business: { level: 1 },
      luck: { level: 1 },
    },
  };
}

function createDefaultEquipment(): EquipmentInstance[] {
  return [
    { id: 'rod', level: 1 },
    { id: 'boat', level: 1 },
    { id: 'bait', level: 1 },
    { id: 'line', level: 1 },
  ];
}

export function createDefaultState(): GameState {
  return {
    version: 7,
    coins: 0,
    totalCoinsEarned: 0,
    fishCaught: 0,
    inventory: [],
    vessels: [],
    vesselTrips: [],
    lastTickTimestamp: Date.now(),
    currentLocation: 'home',
    discoveredLocations: ['home', 'pond', 'market', 'harbor'],
    isFishing: false,
    fishingStartTime: null,
    fishingGeneration: 0,
    playerStats: createDefaultPlayerStats(),
    equipment: createDefaultEquipment(),
    properties: [{ id: 'home', tier: 1 }],
  };
}

export function loadState(): { state: GameState; offlineLog: LogEntry | null } {
  const raw = localStorage.getItem(SAVE_KEY);
  if (!raw) {
    return { state: createDefaultState(), offlineLog: null };
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return {
      state: createDefaultState(),
      offlineLog: {
        text: 'Save file corrupted — starting fresh.',
        type: 'error',
        timestamp: Date.now(),
      },
    };
  }

  if (typeof parsed === 'object' && parsed !== null) {
    const record = parsed as Record<string, unknown>;
    const ver = record.version as number | undefined;
    if (!ver || ver < 2) {
      parsed = migrateV6ToV7(migrateV5ToV6(migrateV4ToV5(migrateV3ToV4(migrateV2ToV3(migrateV1ToV2(record))))));
    } else if (ver === 2) {
      parsed = migrateV6ToV7(migrateV5ToV6(migrateV4ToV5(migrateV3ToV4(migrateV2ToV3(record)))));
    } else if (ver === 3) {
      parsed = migrateV6ToV7(migrateV5ToV6(migrateV4ToV5(migrateV3ToV4(record))));
    } else if (ver === 4) {
      parsed = migrateV6ToV7(migrateV5ToV6(migrateV4ToV5(record)));
    } else if (ver === 5) {
      parsed = migrateV6ToV7(migrateV5ToV6(record));
    } else if (ver === 6) {
      parsed = migrateV6ToV7(record);
    }
  }

  const validated = validateGameState(parsed);
  if (!validated) {
    return {
      state: createDefaultState(),
      offlineLog: {
        text: 'Save file invalid — starting fresh.',
        type: 'error',
        timestamp: Date.now(),
      },
    };
  }

  const now = Date.now();
  const deltaSeconds = (now - validated.lastTickTimestamp) / 1000;
  let offlineLog: LogEntry | null = null;

  if (deltaSeconds > 300) {
    offlineLog = {
      text: `You were away for ${Math.floor(deltaSeconds / 60)}m ${Math.floor(deltaSeconds % 60)}s. Vessels are deactivated while offline.`,
      type: 'info',
      timestamp: now,
    };
  }

  validated.lastTickTimestamp = now;
  validated.vesselTrips = [];
  return { state: validated, offlineLog };
}

export class StateManager {
  private state: GameState;
  private subscribers: Array<(state: GameState, logEntries: LogEntry[]) => void> = [];
  private saveTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(initialState: GameState) {
    this.state = initialState;
  }

  getState(): GameState {
    return this.state;
  }

  subscribe(cb: (state: GameState, logEntries: LogEntry[]) => void): () => void {
    this.subscribers.push(cb);
    return () => {
      this.subscribers = this.subscribers.filter(s => s !== cb);
    };
  }

  dispatch(patch: Partial<GameState>, logEntries: LogEntry[] = []): void {
    this.state = { ...this.state, ...patch, lastTickTimestamp: Date.now() };
    for (const cb of this.subscribers) {
      cb(this.state, logEntries);
    }
    this.debouncedSave();
  }

  update(_deltaSeconds: number): LogEntry[] {
    const now = Date.now();
    const businessLevel = this.state.playerStats.skills.business.level;

    const ownedVesselIds = new Set(this.state.vessels.map(v => v.vesselId));
    this.state.vesselTrips = this.state.vesselTrips.filter(t => ownedVesselIds.has(t.vesselId));

    for (const vesselDefId of new Set(this.state.vessels.map(v => v.vesselId))) {
      const def = getVesselDefById(vesselDefId);
      if (!def) continue;
      const ownedCount = this.state.vessels.filter(v => v.vesselId === vesselDefId).length;
      const activeTrips = this.state.vesselTrips.filter(t => t.vesselId === vesselDefId).length;

      if (activeTrips > ownedCount) {
        const vesselTrips = this.state.vesselTrips.filter(t => t.vesselId === vesselDefId);
        vesselTrips.sort((a, b) => a.endTimestamp - b.endTimestamp);
        const toKeep = vesselTrips.slice(0, ownedCount);
        this.state.vesselTrips = [
          ...this.state.vesselTrips.filter(t => t.vesselId !== vesselDefId),
          ...toKeep,
        ];
      }

      const idleVessels = this.state.vessels.filter(v =>
        v.vesselId === vesselDefId &&
        !this.state.vesselTrips.some(t => t.name === v.name)
      );

      for (const vessel of idleVessels) {
        this.state.vesselTrips.push({
          vesselId: vessel.vesselId,
          name: vessel.name,
          endTimestamp: now + def.tripDurationSeconds * 1000,
        });
      }
    }

    const completedTrips = this.state.vesselTrips.filter(t => t.endTimestamp <= now);
    const remainingTrips = this.state.vesselTrips.filter(t => t.endTimestamp > now);

    if (completedTrips.length === 0) {
      this.debouncedSave();
      return [];
    }

    let profit = 0;
    const tripMessages: string[] = [];
    for (const trip of completedTrips) {
      const tripProfit = getVesselTripProfit(trip.vesselId, businessLevel);
      profit += tripProfit;
      const def = getVesselDefById(trip.vesselId);
      const typeName = def?.name || trip.vesselId;
      tripMessages.push(`${trip.name} (${typeName}) earned ${fmtCoins(tripProfit)}`);
    }
    profit = Math.round(profit * 100) / 100;

    const logEntries: LogEntry[] = [{
      text: `⚓ Fleet returned! ${tripMessages.join(', ')}`,
      type: 'success',
      timestamp: now,
    }];

    this.state = {
      ...this.state,
      vesselTrips: remainingTrips,
      coins: Math.round((this.state.coins + profit) * 100) / 100,
      totalCoinsEarned: Math.round((this.state.totalCoinsEarned + profit) * 100) / 100,
      lastTickTimestamp: now,
    };

    for (const cb of this.subscribers) {
      cb(this.state, logEntries);
    }
    this.debouncedSave();
    return logEntries;
  }

  addPlayerXp(amount: number): LogEntry[] {
    const logEntries: LogEntry[] = [];
    const ps = this.state.playerStats;
    const result = addXpToPlayer(ps.level, ps.xp, ps.xpToNext, amount);

    const newStats = {
      ...ps,
      level: result.level,
      xp: result.xp,
      xpToNext: result.xpToNext,
      unallocatedPoints: ps.unallocatedPoints + (result.leveledUp ? 1 : 0),
    };

    this.state = { ...this.state, playerStats: newStats };

    if (result.leveledUp) {
      logEntries.push({
        text: `You reached level ${result.level}! +1 stat point to allocate.`,
        type: 'success',
        timestamp: Date.now(),
      });
    }

    this.debouncedSave();
    return logEntries;
  }

  reset(): LogEntry {
    this.state = createDefaultState();
    localStorage.removeItem(SAVE_KEY);
    return {
      text: 'Game reset. All progress lost.',
      type: 'system',
      timestamp: Date.now(),
    };
  }

  private debouncedSave(): void {
    if (this.saveTimer !== null) {
      clearTimeout(this.saveTimer);
    }
    this.saveTimer = setTimeout(() => {
      localStorage.setItem(SAVE_KEY, JSON.stringify(this.state));
      this.saveTimer = null;
    }, SAVE_DEBOUNCE_MS);
  }

  forceSave(): void {
    if (this.saveTimer !== null) {
      clearTimeout(this.saveTimer);
      this.saveTimer = null;
    }
    localStorage.setItem(SAVE_KEY, JSON.stringify(this.state));
  }
}
